import * as React from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  Award,
  CalendarCheck,
  CalendarDays,
  ChevronsUpDown,
  ClipboardCheck,
  UserCheck,
  Dices,
  FileText,
  GraduationCap,
  HeartHandshake,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquare,
  Moon,
  PenTool,
  Presentation,
  Radar,
  Shield,
  Sun,
  X,
} from 'lucide-react'
import { useAuth } from '@/auth/auth-provider'
import { cn } from '@/lib/utils'
import { displayName, initials } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { BrandMark } from '@/components/brand-mark'
import { PresentationInvite } from '@/components/presentation-invite'
import { LobbyInvite } from '@/components/team-lobby'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { featureOn, type TeamFeature } from '@/lib/features'
import { useTodayGate } from '@/hooks/use-today'
import { useTheme } from '@/components/theme-provider'

// `feature` names the per-team switch that hides an entry (0044). Entries
// without one are always present: Dashboard is the fallback route, and
// Evaluations and Boards have no switch.
const NAV: Array<{
  to: string
  label: string
  icon: typeof LayoutDashboard
  feature?: TeamFeature['key']
}> = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/activities', label: 'Topics', icon: Presentation, feature: 'topics_enabled' },
  { to: '/schedule', label: 'Schedule', icon: CalendarDays, feature: 'schedule_enabled' },
  { to: '/evaluations', label: 'Evaluations', icon: ClipboardCheck },
  // Every member's own; nobody else on the team reads it (0067).
  { to: '/self-assessment', label: 'Self-assessment', icon: UserCheck },
  { to: '/workshops', label: 'Workshops', icon: GraduationCap, feature: 'workshops_enabled' },
  { to: '/discussion', label: 'Discussion', icon: MessageSquare, feature: 'discussion_enabled' },
  { to: '/boards', label: 'Boards', icon: PenTool },
]

/**
 * Whether the desktop sidebar is icons-only. Remembered, because it's a
 * workspace preference rather than a per-page one: someone who collapsed it to
 * get more canvas for a board does not want it back at full width on the next
 * navigation, or tomorrow.
 */
const COLLAPSE_KEY = 'teamaccel-nav-collapsed'

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1'
  } catch {
    // Private windows and blocked site data must not take the shell down.
    return false
  }
}

export function AppShell() {
  const { isAdmin, isLead, isCoach, currentTeam } = useAuth()
  // Whether there is a Today at all. Two small reads, re-asked on navigation —
  // see hooks/use-today.ts for why the sidebar asks separately from the page.
  const today = useTodayGate()
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [collapsed, setCollapsed] = React.useState(readCollapsed)
  const location = useLocation()

  // Close the drawer on navigation, otherwise it stays over the new page.
  React.useEffect(() => setMobileOpen(false), [location.pathname])

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0')
      } catch {
        /* best effort — the state still applies for this session */
      }
      return next
    })
  }

  // Drop the sections this team has switched off (0044). This runs FIRST, so
  // the role-gated entries below are appended to an already-filtered list and
  // each one only has to think about its own audience.
  const enabledNav = NAV.filter((n) => !n.feature || featureOn(currentTeam, n.feature))

  // Today comes FIRST, above the dashboard, and only on a day there is
  // something on: a tab that is there most mornings and gone the rest of the
  // time is read as "is anything happening", which is the question it answers.
  // Absent while the answer is unknown rather than present-and-maybe-empty —
  // it appears a beat after the shell on the days it exists, which is quieter
  // than appearing on every page load and vanishing again.
  const dayNav = today.has
    ? [{ to: '/today', label: 'Today', icon: CalendarCheck }, ...enabledNav]
    : enabledNav

  // Reports are lead-and-coach only, so the entry is absent for a member rather
  // than present-and-empty. Still only a courtesy — the policy in
  // migrations/0018_reports.sql is what actually withholds them. The feature
  // switch stacks on top: off hides it from the lead too, on hands the decision
  // back to the policy.
  const teamNav =
    isLead && featureOn(currentTeam, 'reports_enabled')
      ? [...dayNav, { to: '/reports', label: 'Reports', icon: FileText }]
      : dayNav

  // Coaching is narrower still: a coach writes it and nobody else — not the
  // lead, not the person it is about (migrations/0031_coaching.sql). Sits after
  // Reports because it is the other half of the same coach's job.
  const coachNav =
    isCoach && featureOn(currentTeam, 'coaching_enabled')
      ? [...teamNav, { to: '/coaching', label: 'Coaching', icon: HeartHandshake }]
      : teamNav

  // Programs are per-team and off by default (migrations/0037). The tab
  // appears for everyone on a team that has them on, because walking the path
  // is a member's job; only the coach-only status page is narrower.
  //
  // "Programs" is the LABEL; the table, the columns, the routes and the types
  // all still say certification. Same split as Topics/`activities` — see
  // AGENTS.md. Do not finish the job unless someone asks: a rename reaches
  // five tables, two RPCs, a public URL people have been given, and every
  // certificate already issued.
  const certNav = currentTeam?.certifications_enabled
    ? [...coachNav, { to: '/certifications', label: 'Programs', icon: Award }]
    : coachNav

  // Games, likewise off until asked for (0046). Last of the team-scoped
  // entries, because it is the one nobody needs to find in a hurry.
  const gamesNav = featureOn(currentTeam, 'games_enabled')
    ? [...certNav, { to: '/games', label: 'Games', icon: Dices }]
    : certNav

  // The DPL-only entries sit after the team-scoped ones because they step
  // outside the current team rather than belonging to it.
  const nav = isAdmin
    ? [
        ...gamesNav,
        { to: '/admin', label: 'Admin', icon: Shield },
        { to: '/mission-control', label: 'Mission Control', icon: Radar },
      ]
    : gamesNav

  return (
    // print:bg-white — the muted app surface is a screen affordance, and it
    // otherwise prints as a full-page gray wash behind a monthly report.
    <div className="flex min-h-screen bg-muted/30 print:bg-white">
      {/* Desktop sidebar. Width is the only thing that changes — the nav stays
          mounted, so collapsing never remounts a page or drops scroll. */}
      <aside
        className={cn(
          // `sticky top-0 h-dvh` is what keeps the rail on screen. Without it
          // the aside stretches to the height of the DOCUMENT, so on a long
          // page the team chip and the build stamp sit at the bottom of the
          // page rather than the bottom of the window, and you have to scroll
          // to the end of a report to find out which team you are in.
          //
          // The height must be the viewport's, not `min-h-screen`'s: the nav
          // inside is `flex-1 overflow-y-auto`, and it can only scroll within a
          // container that has a bounded height. dvh rather than vh so a mobile
          // browser's collapsing address bar does not cut the footer off — this
          // rail is desktop-only today, but that is a layout accident, not a
          // promise.
          'no-print sticky top-0 hidden h-dvh shrink-0 flex-col border-r bg-background transition-[width] duration-200 lg:flex',
          // w-14 is not arbitrary. Nav items keep their `px-3` in both states so
          // the icons never move, which puts an icon's centre at 8px (nav
          // padding) + 12px (px-3) + 8px (half a 16px icon) = 28px. Twice that
          // is 56px = w-14, so the icons are both centred in the collapsed rail
          // AND in exactly the spot they occupy when expanded. Widening this
          // without re-doing that arithmetic is what makes them jump.
          collapsed ? 'w-14' : 'w-60',
        )}
      >
        <SidebarContent nav={nav} collapsed={collapsed} onToggle={toggleCollapsed} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col border-r bg-background shadow-xl">
            <div className="flex justify-end p-2">
              <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} aria-label="Close menu">
                <X />
              </Button>
            </div>
            {/* Never collapsed: the drawer is already a deliberate reveal, and
                an icons-only overlay taking a third of a phone screen helps
                nobody. So no toggle here either. */}
            <SidebarContent nav={nav} collapsed={false} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu />
          </Button>
          <TeamSwitcher />
          <div className="flex-1" />
          <ThemeToggle />
          <UserMenu />
        </header>

        {/* Mounted on the shell rather than on any one page, so a live session
            reaches you wherever you are. Deliberately NOT reachable from inside
            a deck: /activities/:id/present renders outside AppShell, so someone
            already watching can't be invited to what they're watching. */}
        <PresentationInvite />
        {/* "Bob's starting Missile Defence, want to join?" — same reasoning:
            on the shell, so it reaches you on whatever page you are on. */}
        <LobbyInvite />

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

function SidebarContent({
  nav,
  collapsed,
  onToggle,
}: {
  nav: typeof NAV
  collapsed: boolean
  /** Absent in the mobile drawer, where there is nothing to collapse. */
  onToggle?: () => void
}) {
  const { currentTeam } = useAuth()

  // The brand mark is the toggle. A plain <button> rather than the Button
  // component so the logo keeps its own size and nothing paints a hover box
  // around a wordmark.
  const brand = (
    <button
      type="button"
      onClick={onToggle}
      disabled={!onToggle}
      aria-expanded={!collapsed}
      aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
      className={cn(
        // px-3.5 in BOTH states, so the mark doesn't move when the rail
        // collapses: 14px + half of the 28px mark is 28px, the centre of the
        // collapsed rail. Same reasoning as the nav padding below.
        'flex h-14 shrink-0 items-center gap-2.5 overflow-hidden px-3.5 text-left',
        onToggle && 'cursor-pointer rounded-md transition-opacity hover:opacity-80',
      )}
    >
      <BrandMark className="h-7 w-7 shrink-0" />
      {!collapsed && (
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold leading-tight">Team Accelerator</div>
          <div className="truncate text-[11px] leading-tight text-muted-foreground">
            Don&apos;t Panic Labs
          </div>
        </div>
      )}
    </button>
  )

  return (
    <>
      {/* Collapsed, the icon alone doesn't say what clicking it does, so the
          tooltip carries that. Expanded, the wordmark is self-evident. */}
      {collapsed && onToggle ? (
        <Tooltip>
          <TooltipTrigger asChild>{brand}</TooltipTrigger>
          <TooltipContent side="right">Expand navigation</TooltipContent>
        </Tooltip>
      ) : (
        brand
      )}
      <Separator />
      <nav className="flex-1 space-y-0.5 overflow-y-auto p-2">
        {nav.map(({ to, label, icon: Icon }) => {
          const link = (
            <NavLink
              key={to}
              to={to}
              // The label is still rendered when collapsed, hidden from sight
              // but not from screen readers — an icon-only link with no
              // accessible name is just a mystery box to anyone not using eyes.
              className={({ isActive }) =>
                cn(
                  // px-3 regardless of state — this is what holds the icons
                  // still. See the width note on <aside>.
                  'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className={collapsed ? 'sr-only' : undefined}>{label}</span>
            </NavLink>
          )
          return collapsed ? (
            <Tooltip key={to}>
              <TooltipTrigger asChild>{link}</TooltipTrigger>
              <TooltipContent side="right">{label}</TooltipContent>
            </Tooltip>
          ) : (
            link
          )
        })}
      </nav>
      {currentTeam &&
        (collapsed ? (
          // The team name doesn't fit, but which team you're in is too important
          // to drop silently — the initial holds the place and the tooltip has
          // the rest. The header's TeamSwitcher still shows it in full.
          <Tooltip>
            <TooltipTrigger asChild>
              {/* px-3.5 puts this 28px chip on the same centre line as the
                  brand mark and the nav icons above it. */}
              <div className="flex cursor-default border-t px-3.5 py-3">
                <span className="flex h-7 w-7 items-center justify-center rounded bg-muted text-[11px] font-semibold">
                  {currentTeam.name.slice(0, 2).toUpperCase()}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">
              <div>{currentTeam.name}</div>
              {currentTeam.company && (
                <div className="text-muted-foreground">{currentTeam.company}</div>
              )}
            </TooltipContent>
          </Tooltip>
        ) : (
          <div className="border-t p-3">
            <div className="truncate text-xs font-medium">{currentTeam.name}</div>
            {currentTeam.company && (
              <div className="truncate text-[11px] text-muted-foreground">{currentTeam.company}</div>
            )}
          </div>
        ))}

      <BuildStamp collapsed={collapsed} />
    </>
  )
}

/**
 * "Is this the build I just made?"
 *
 * Asked constantly on this project, because `spa/` IS the deployed artifact and
 * a stale build looks exactly like a working one — you edit, forget to run
 * ./scripts/build-spa.sh, reload, and see the old app behaving correctly.
 *
 * The number is the commit count, so it reads as a version a person can compare
 * ("214 is newer than 210"); the SHA identifies the source; a trailing `+` means
 * the tree had uncommitted changes, so what is running is in no commit at all.
 * All three are inlined at build time — see the note in vite.config.ts.
 *
 * Deliberately quiet: this is a diagnostic, not information anyone needs while
 * doing their job, so it sits below the team block in the smallest readable
 * type and never competes with the navigation.
 */
function BuildStamp({ collapsed }: { collapsed: boolean }) {
  const label = `Build ${__BUILD_NUMBER__} · ${__BUILD_DATE__}`
  const detail = `${label} · ${__BUILD_SHA__}`

  // Collapsed, the rail is 56px wide and none of this fits. The number alone
  // still answers the question most of the time, and the tooltip has the rest.
  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-default border-t px-2 py-2 text-center text-[9px] leading-tight text-muted-foreground/60">
            {__BUILD_NUMBER__}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right">{detail}</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-default border-t px-3 py-2 text-[10px] leading-tight text-muted-foreground/60">
          {label}
        </div>
      </TooltipTrigger>
      {/* The SHA lives here rather than on the face of it: it is the thing you
          want when comparing against git, and noise the rest of the time. */}
      <TooltipContent side="right">{detail}</TooltipContent>
    </Tooltip>
  )
}

function TeamSwitcher() {
  const { memberships, currentTeam, setCurrentTeamId, currentRole, isAdmin, refresh } = useAuth()
  const navigate = useNavigate()

  /**
   * Switching teams lands on the dashboard, and re-reads the roster.
   *
   * Both halves are about not leaving someone looking at the previous team's
   * data. Most routes here are detail pages keyed on an id belonging to ONE
   * team — /activities/:id, /boards/:id, /reports/:id — and RLS answers a read
   * for another team's row with nothing rather than an error, so staying put
   * renders an empty or half-populated page that reads as a bug. The dashboard
   * is the one screen that means the same thing for every team.
   *
   * The refresh is for role, not for rows: `isLead` / `isCoach` come from the
   * membership list, and they gate which nav entries and routes exist. Someone
   * who is a lead on one team and a plain member on another needs that
   * recomputed from fresh data, not from whatever was loaded at sign-in. It is
   * deliberately not awaited — `load()` never raises `loading`, so navigation
   * shouldn't wait on a round trip to feel instant.
   *
   * Matches what Mission Control already does when it switches teams
   * (pages/mission-control.tsx).
   */
  function switchTo(teamId: string) {
    // Re-picking the current team should not yank someone off the page they're
    // on — the menu closing is the whole of the expected outcome.
    if (teamId === currentTeam?.id) return
    setCurrentTeamId(teamId)
    navigate('/dashboard')
    void refresh()
  }

  if (!currentTeam) return null

  // One team, and no Mission Control to offer — nothing to choose, so show a
  // label rather than a control that does nothing when clicked.
  if (memberships.length === 1 && !isAdmin) {
    return (
      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate text-sm font-semibold">{currentTeam.name}</span>
        <RoleBadge role={currentRole} />
      </div>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="-ml-2 min-w-0 gap-2">
          <span className="truncate font-semibold">{currentTeam.name}</span>
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-[70vh] w-64 overflow-y-auto">
        <DropdownMenuLabel className="text-xs text-muted-foreground">Switch team</DropdownMenuLabel>
        {memberships.map(({ team, role }) => (
          <DropdownMenuItem
            key={team.id}
            onSelect={() => switchTo(team.id)}
            className={cn('gap-2', team.id === currentTeam.id && 'bg-accent')}
          >
            <div className="min-w-0 flex-1">
              <div className="truncate">{team.name}</div>
              {team.company && (
                <div className="truncate text-xs text-muted-foreground">{team.company}</div>
              )}
            </div>
            <RoleBadge role={role} />
          </DropdownMenuItem>
        ))}
        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate('/mission-control')}>
              <Radar /> Mission Control
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function RoleBadge({ role }: { role: string | null }) {
  if (!role || role === 'member') return null
  return (
    <Badge variant="muted" className="shrink-0 text-[10px]">
      {role === 'team_lead' ? 'Lead' : 'DPL'}
    </Badge>
  )
}

function ThemeToggle() {
  const { theme, setTheme } = useTheme()
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
    >
      {theme === 'dark' ? <Sun /> : <Moon />}
    </Button>
  )
}

function UserMenu() {
  const { profile, session, signOut, isAdmin } = useAuth()
  const navigate = useNavigate()
  const label = displayName(profile ?? (session?.user?.email ? { display_name: null, email: session.user.email } : null))

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full" aria-label="Account">
          <Avatar className="h-8 w-8">
            <AvatarFallback>{initials(profile?.display_name, profile?.email ?? session?.user?.email)}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="truncate text-sm">{label}</div>
          <div className="truncate text-xs font-normal text-muted-foreground">
            {profile?.email ?? session?.user?.email}
          </div>
          {isAdmin && (
            <Badge variant="secondary" className="mt-1.5 text-[10px]">
              Don&apos;t Panic Labs
            </Badge>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* Not in the sidebar: this is about the person, not the team, and it's
            rarely visited — the account menu is where people look for it.
            DPL staff only, matching the RLS policy that governs the mint; hiding
            it is the courtesy, migrations/0016 is the control. */}
        {isAdmin && (
          <DropdownMenuItem onSelect={() => navigate('/access')}>
            <KeyRound /> CLI &amp; Claude access
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => void signOut()}>
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
